import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../components/SubscriptionGuard';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import { getInitials } from '../lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { toast } from 'sonner';
import { 
  Building2, 
  Calendar, 
  Clock, 
  Shield, 
  Link2, 
  AlertTriangle,
  CreditCard,
  History,
  Save,
  ChevronDown,
  ChevronRight,
  Check,
  X,
  Upload,
  Camera,
  Globe,
  Users,
  FileText,
  Trash2,
  Download,
  ExternalLink,
  Target,
  Info,
  Receipt,
  Play,
  MapPin,
  Lock,
  Crown
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const WorkspaceSettings = () => {
  const { api, user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { subscription } = useSubscription() || {};
  const isFreePlan = subscription?.plan === 'free';
  
  // Section open states
  const [companyOpen, setCompanyOpen] = useState(true);
  const [billingDetailsOpen, setBillingDetailsOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(true);
  const [timesheetOpen, setTimesheetOpen] = useState(true);
  const [approvalOpen, setApprovalOpen] = useState(true);
  const [rolesOpen, setRolesOpen] = useState(false);
  const [dangerOpen, setDangerOpen] = useState(false);
  const [billingOpen, setBillingOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);

  // Settings state
  const [settings, setSettings] = useState({
    company_name: '',
    company_logo: null,
    default_timezone: 'UTC',
    default_language: 'en',
    company_website: '',
    working_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    daily_hours_target: 8,
    submission_deadline_day: 'friday',
    submission_deadline_time: '17:00',
    grace_period_hours: 24,
    timesheets_require_approval: true,
    employees_can_edit_submitted: false,
    timesheet_approver: 'direct_manager',
    time_off_approver: 'direct_manager',
    time_off_requires_approval: true,
    max_time_off_days_per_request: 14,
    manager_can_create_projects: true,
    manager_can_approve_timesheets: true,
    manager_can_approve_time_off: true,
    manager_can_view_all_reports: true,
    manager_can_manage_people: true,
    screen_capture_frequency: 10, // Fix 50: Default 10 minutes
    // Fix 51: Idle detection defaults
    idle_detection_enabled: true,
    idle_threshold: 10,
    idle_action: 'auto_pause',
    // Fix 54: Daily hours target defaults
    daily_hours_target_enabled: false,
    daily_hours_target_default: 8,
    daily_hours_target_max: 12,
    slack_connected: false,
    jira_connected: false,
    google_workspace_connected: false,
    next_billing_date: null
  });

  const [originalSettings, setOriginalSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Helper function for avatar colors
  const avatarColors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];
  const getAvatarColor = (name) => {
    if (!name) return avatarColors[0];
    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return avatarColors[hash % avatarColors.length];
  };

  // Check if user is admin
  useEffect(() => {
    if (user && user.role !== 'admin') {
      if (user.role === 'manager') {
        toast.error('You do not have permission to access Workspace Settings');
        navigate('/admin/dashboard');
      } else {
        toast.error('Admin access required');
        navigate('/dashboard');
      }
    }
  }, [user, navigate]);

  // Fetch settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const [settingsRes, auditRes, paymentRes] = await Promise.all([
          api.get('/admin/workspace-settings'),
          api.get('/admin/audit-log'),
          api.get('/payments/status').catch(() => ({ data: null }))
        ]);
        setSettings(settingsRes.data);
        setOriginalSettings(settingsRes.data);
        setAuditLog(auditRes.data);
        if (paymentRes.data) setSubscriptionInfo(paymentRes.data);
      } catch (error) {
        console.error('Failed to fetch settings:', error);
        toast.error('Failed to load workspace settings');
      } finally {
        setLoading(false);
      }
    };

    if (user?.role === 'admin') {
      fetchSettings();
    }
  }, [api, user]);

  // Track changes
  useEffect(() => {
    if (originalSettings) {
      const changed = JSON.stringify(settings) !== JSON.stringify(originalSettings);
      setHasChanges(changed);
    }
  }, [settings, originalSettings]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await api.put('/admin/workspace-settings', settings);
      setSettings(response.data);
      setOriginalSettings(response.data);
      setHasChanges(false);
      toast.success('Settings saved successfully');
      
      // Refresh audit log
      const auditRes = await api.get('/admin/audit-log');
      setAuditLog(auditRes.data);
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      toast.error('Please upload a JPG or PNG image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        setSettings(prev => ({ ...prev, company_logo: e.target.result }));
        setUploadingLogo(false);
      };
      reader.onerror = () => {
        toast.error('Failed to read image');
        setUploadingLogo(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload logo');
      setUploadingLogo(false);
    }
  };

  const handleWorkingDayToggle = (day) => {
    setSettings(prev => ({
      ...prev,
      working_days: prev.working_days.includes(day)
        ? prev.working_days.filter(d => d !== day)
        : [...prev.working_days, day]
    }));
  };

  const handleExportData = async () => {
    try {
      const response = await api.get('/admin/workspace-settings/export-data', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `workspace-export-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Data exported successfully');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const handleDeleteWorkspace = async () => {
    if (deleteConfirmation !== settings.company_name) {
      toast.error('Please type the workspace name correctly');
      return;
    }

    try {
      await api.delete('/admin/workspace', { data: { confirmation_name: deleteConfirmation } });
      toast.success('Workspace deleted');
      // Logout and redirect
      window.location.href = '/login';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete workspace');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const days = [
    { value: 'monday', label: 'Mon' },
    { value: 'tuesday', label: 'Tue' },
    { value: 'wednesday', label: 'Wed' },
    { value: 'thursday', label: 'Thu' },
    { value: 'friday', label: 'Fri' },
    { value: 'saturday', label: 'Sat' },
    { value: 'sunday', label: 'Sun' }
  ];

  const timezones = [
    { value: 'UTC', label: 'UTC' },
    { value: 'America/New_York', label: 'Eastern Time (US)' },
    { value: 'America/Chicago', label: 'Central Time (US)' },
    { value: 'America/Denver', label: 'Mountain Time (US)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (US)' },
    { value: 'Europe/London', label: 'London' },
    { value: 'Europe/Paris', label: 'Paris' },
    { value: 'Asia/Tokyo', label: 'Tokyo' },
    { value: 'Asia/Shanghai', label: 'Shanghai' },
    { value: 'Asia/Kolkata', label: 'India (IST)' },
    { value: 'Australia/Sydney', label: 'Sydney' }
  ];

  const languages = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'fr', label: 'French' },
    { value: 'de', label: 'German' },
    { value: 'ja', label: 'Japanese' },
    { value: 'zh', label: 'Chinese' }
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  if (user?.role !== 'admin') {
    return null;
  }

  return (
    <Layout>
      <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="workspace-settings-page">
        {/* Header with Save Button */}
        <div className="flex items-center justify-between sticky top-0 z-20 bg-white dark:bg-slate-950 py-4 -mt-4 border-b border-slate-200 dark:border-slate-800">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                  {t('workspace.title')}
                </h1>
                <p className="text-sm text-slate-500">
                  {t('workspace.subtitle')}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {hasChanges && (
              <span className="text-sm text-amber-600 bg-amber-50 dark:bg-amber-900/10 px-3 py-1 rounded-full">
                Unsaved changes
              </span>
            )}
            <Button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className="btn-premium h-10"
              data-testid="save-settings-btn"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>

        {/* PRIORITY 1: Company Profile */}
        <Collapsible open={companyOpen} onOpenChange={setCompanyOpen}>
          <div className="premium-card" data-testid="company-profile-section">
            <CollapsibleTrigger className="w-full p-6 flex items-center justify-between cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-purple-600" />
                <div className="text-left">
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('workspace.companyProfile')}</h2>
                  <p className="text-sm text-slate-500">Basic company information</p>
                </div>
              </div>
              {companyOpen ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="px-6 pb-6 space-y-6 border-t border-slate-100 dark:border-slate-800 pt-6">
                {/* Logo Upload */}
                <div className="flex items-center gap-6">
                  <div className="relative group">
                    <Avatar className="h-20 w-20 ring-4 ring-white dark:ring-slate-800 shadow-xl">
                      {settings.company_logo ? (
                        <AvatarImage src={settings.company_logo} alt="Company Logo" />
                      ) : (
                        <AvatarFallback className="bg-gradient-to-br from-purple-600 to-indigo-600 text-white text-2xl font-bold">
                          {settings.company_name?.charAt(0) || 'C'}
                        </AvatarFallback>
                      )}
                    </Avatar>
                    <label className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                      {uploadingLogo ? (
                        <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Camera className="w-6 h-6 text-white" />
                      )}
                    </label>
                    <input
                      type="file"
                      accept="image/jpeg,image/png"
                      onChange={handleLogoUpload}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      data-testid="logo-upload-input"
                    />
                    <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-purple-600 rounded-full flex items-center justify-center shadow-lg border-2 border-white dark:border-slate-800">
                      <Upload className="w-3.5 h-3.5 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Company Logo</h3>
                    <p className="text-xs text-slate-500 mt-1">JPG or PNG, max 5MB</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-sm font-medium">Company Name</Label>
                    <Input
                      value={settings.company_name}
                      onChange={(e) => setSettings(prev => ({ ...prev, company_name: e.target.value }))}
                      placeholder="Enter company name"
                      className="mt-2 h-11"
                      data-testid="company-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Company Website (optional)</Label>
                    <Input
                      value={settings.company_website}
                      onChange={(e) => setSettings(prev => ({ ...prev, company_website: e.target.value }))}
                      placeholder="https://example.com"
                      className="mt-2 h-11"
                      data-testid="company-website-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Default Timezone</Label>
                    <Select
                      value={settings.default_timezone}
                      onValueChange={(value) => setSettings(prev => ({ ...prev, default_timezone: value }))}
                    >
                      <SelectTrigger className="mt-2 h-11" data-testid="timezone-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {timezones.map(tz => (
                          <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Default Language</Label>
                    <Select
                      value={settings.default_language}
                      onValueChange={(value) => setSettings(prev => ({ ...prev, default_language: value }))}
                    >
                      <SelectTrigger className="mt-2 h-11" data-testid="language-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {languages.map(lang => (
                          <SelectItem key={lang.value} value={lang.value}>{lang.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>

        {/* Billing & GST Details */}
        <Collapsible open={billingDetailsOpen} onOpenChange={setBillingDetailsOpen}>
          <div className="premium-card" data-testid="billing-details-section">
            <CollapsibleTrigger className="w-full p-6 flex items-center justify-between cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
              <div className="flex items-center gap-3">
                <Receipt className="w-5 h-5 text-emerald-600" />
                <div className="text-left">
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Billing & GST Details</h2>
                  <p className="text-sm text-slate-500">Company billing address and GSTIN for invoices</p>
                </div>
              </div>
              {billingDetailsOpen ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="px-6 pb-6 space-y-6 border-t border-slate-100 dark:border-slate-800 pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-sm font-medium">Legal Company Name (for Invoice)</Label>
                    <Input
                      value={settings.billing_company_name || ''}
                      onChange={(e) => setSettings(prev => ({ ...prev, billing_company_name: e.target.value }))}
                      placeholder="Registered company name"
                      className="mt-2 h-11"
                      data-testid="billing-company-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Company Currency {originalSettings?.company_currency && <span className="text-xs text-slate-400 font-normal ml-1">(locked)</span>}</Label>
                    {originalSettings?.company_currency ? (
                      <>
                        <Select value={settings.company_currency || 'INR'} disabled>
                          <SelectTrigger className="mt-2 h-11 opacity-60 cursor-not-allowed" data-testid="company-currency-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={settings.company_currency || 'INR'}>{settings.company_currency || 'INR'}</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-slate-400 mt-1">Base currency cannot be changed once set. Contact support if you need to change it.</p>
                      </>
                    ) : (
                      <>
                        <Select value={settings.company_currency || 'INR'} onValueChange={v => setSettings(prev => ({ ...prev, company_currency: v }))}>
                          <SelectTrigger className="mt-2 h-11" data-testid="company-currency-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="INR">INR - Indian Rupee</SelectItem>
                            <SelectItem value="USD">USD - US Dollar</SelectItem>
                            <SelectItem value="EUR">EUR - Euro</SelectItem>
                            <SelectItem value="GBP">GBP - British Pound</SelectItem>
                            <SelectItem value="AED">AED - UAE Dirham</SelectItem>
                            <SelectItem value="SGD">SGD - Singapore Dollar</SelectItem>
                            <SelectItem value="AUD">AUD - Australian Dollar</SelectItem>
                            <SelectItem value="CAD">CAD - Canadian Dollar</SelectItem>
                          </SelectContent>
                        </Select>
                        <div className="mt-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 rounded-lg">
                          <p className="text-xs text-amber-700 font-medium">Choose carefully — the base currency cannot be changed once saved. All invoice rollup totals (Total Invoiced, Collected, Outstanding) will be reported in this currency.</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-sm font-medium">GSTIN</Label>
                    <Input
                      value={settings.gst_number || ''}
                      onChange={(e) => setSettings(prev => ({ ...prev, gst_number: e.target.value.toUpperCase() }))}
                      placeholder="e.g. 22AAAAA0000A1Z5"
                      className="mt-2 h-11 uppercase"
                      maxLength={15}
                      data-testid="gst-number-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-sm font-medium">Address Line 1</Label>
                    <Input
                      value={settings.billing_address_line1 || ''}
                      onChange={(e) => setSettings(prev => ({ ...prev, billing_address_line1: e.target.value }))}
                      placeholder="Street address"
                      className="mt-2 h-11"
                      data-testid="billing-address1-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Address Line 2</Label>
                    <Input
                      value={settings.billing_address_line2 || ''}
                      onChange={(e) => setSettings(prev => ({ ...prev, billing_address_line2: e.target.value }))}
                      placeholder="Apartment, suite, etc. (optional)"
                      className="mt-2 h-11"
                      data-testid="billing-address2-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <Label className="text-sm font-medium">City</Label>
                    <Input
                      value={settings.billing_city || ''}
                      onChange={(e) => setSettings(prev => ({ ...prev, billing_city: e.target.value }))}
                      placeholder="City"
                      className="mt-2 h-11"
                      data-testid="billing-city-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">State</Label>
                    <Input
                      value={settings.billing_state || ''}
                      onChange={(e) => setSettings(prev => ({ ...prev, billing_state: e.target.value }))}
                      placeholder="State"
                      className="mt-2 h-11"
                      data-testid="billing-state-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Pincode</Label>
                    <Input
                      value={settings.billing_pincode || ''}
                      onChange={(e) => setSettings(prev => ({ ...prev, billing_pincode: e.target.value }))}
                      placeholder="Pincode"
                      className="mt-2 h-11"
                      maxLength={6}
                      data-testid="billing-pincode-input"
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-400">These details will appear on your GST invoices. Click Save at the top to apply changes.</p>
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>

        {/* Data Region */}
        <div className="premium-card" data-testid="data-region-section">
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <MapPin className="w-5 h-5 text-blue-600" />
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Data Region</h2>
                <p className="text-sm text-slate-500">Where your organization's data is stored</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-green-600 flex items-center justify-center shadow-sm flex-shrink-0">
                <span className="text-white text-lg font-bold">IN</span>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-white">India</h3>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400">
                    <Check className="w-3 h-3" /> Active
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">All your data is securely stored in Indian data centers</p>
              </div>
              <div className="flex items-center gap-1.5 text-slate-400">
                <Lock className="w-4 h-4" />
              </div>
            </div>
            <div className="mt-3 flex items-start gap-2 px-1">
              <Info className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-slate-400">
                Data region cannot be changed for your current plan. Upgrade to <span className="font-semibold text-purple-500">Premium</span> to choose from multiple regions including US, EU, and more.
              </p>
            </div>
          </div>
        </div>

        {/* Replay Setup Tour */}
        <div className="premium-card p-5 flex items-center justify-between" data-testid="replay-tour-section">
          <div className="flex items-center gap-3">
            <Play className="w-5 h-5 text-blue-600" />
            <div>
              <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Replay Setup Tour</h2>
              <p className="text-xs text-slate-500">Re-watch the personalized onboarding walkthrough</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                await api.put('/admin/workspace-settings', { setup_completed: false });
                navigate('/admin/dashboard');
              } catch { /* ignore */ }
            }}
            className="text-blue-600 border-blue-200 hover:bg-blue-50 dark:bg-blue-900/30"
            data-testid="replay-tour-btn"
          >
            <Play className="w-4 h-4 mr-1.5" /> Replay Tour
          </Button>
        </div>

        {/* PRIORITY 1: Work Schedule */}
        <Collapsible open={scheduleOpen} onOpenChange={setScheduleOpen}>
          <div className="premium-card" data-testid="work-schedule-section">
            <CollapsibleTrigger className="w-full p-6 flex items-center justify-between cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-blue-600" />
                <div className="text-left">
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('workspace.workSchedule')}</h2>
                  <p className="text-sm text-slate-500">Default working days and hours</p>
                </div>
              </div>
              {scheduleOpen ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="px-6 pb-6 space-y-6 border-t border-slate-100 dark:border-slate-800 pt-6">
                <div>
                  <Label className="text-sm font-medium mb-3 block">Working Days</Label>
                  <div className="flex flex-wrap gap-2">
                    {days.map(day => (
                      <button
                        key={day.value}
                        onClick={() => handleWorkingDayToggle(day.value)}
                        className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                          settings.working_days.includes(day.value)
                            ? 'bg-blue-600 text-white shadow-md'
                            : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200'
                        }`}
                        data-testid={`working-day-${day.value}`}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-sm font-medium">Daily Hours Target</Label>
                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                      {settings.daily_hours_target}h
                    </span>
                  </div>
                  <Slider
                    value={[settings.daily_hours_target]}
                    onValueChange={([value]) => setSettings(prev => ({ ...prev, daily_hours_target: value }))}
                    min={1}
                    max={12}
                    step={0.5}
                    className="w-full"
                    data-testid="daily-hours-slider"
                  />
                  <div className="flex justify-between text-xs text-slate-400 mt-1">
                    <span>1h</span>
                    <span>12h</span>
                  </div>
                </div>
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>

        {/* PRIORITY 1: Timesheet Rules */}
        {!isFreePlan && (
        <Collapsible open={timesheetOpen} onOpenChange={setTimesheetOpen}>
          <div className="premium-card" data-testid="timesheet-rules-section">
            <CollapsibleTrigger className="w-full p-6 flex items-center justify-between cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-emerald-600" />
                <div className="text-left">
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('workspace.timesheetRules')}</h2>
                  <p className="text-sm text-slate-500">Submission deadlines and policies</p>
                </div>
              </div>
              {timesheetOpen ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="px-6 pb-6 space-y-6 border-t border-slate-100 dark:border-slate-800 pt-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <Label className="text-sm font-medium">Submission Deadline Day</Label>
                    <Select
                      value={settings.submission_deadline_day}
                      onValueChange={(value) => setSettings(prev => ({ ...prev, submission_deadline_day: value }))}
                    >
                      <SelectTrigger className="mt-2 h-11" data-testid="deadline-day-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {days.map(day => (
                          <SelectItem key={day.value} value={day.value} className="capitalize">{day.value}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Submission Deadline Time</Label>
                    <Input
                      type="time"
                      value={settings.submission_deadline_time}
                      onChange={(e) => setSettings(prev => ({ ...prev, submission_deadline_time: e.target.value }))}
                      className="mt-2 h-11"
                      data-testid="deadline-time-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Grace Period (hours)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="72"
                      value={settings.grace_period_hours}
                      onChange={(e) => setSettings(prev => ({ ...prev, grace_period_hours: parseInt(e.target.value) || 0 }))}
                      className="mt-2 h-11"
                      data-testid="grace-period-input"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                    <div>
                      <h4 className="text-sm font-medium text-slate-900 dark:text-white">Timesheets Require Approval</h4>
                      <p className="text-xs text-slate-500 mt-0.5">Managers must approve before timesheets are finalized</p>
                    </div>
                    <Switch
                      checked={settings.timesheets_require_approval}
                      onCheckedChange={(checked) => setSettings(prev => ({ ...prev, timesheets_require_approval: checked }))}
                      data-testid="require-approval-toggle"
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                    <div>
                      <h4 className="text-sm font-medium text-slate-900 dark:text-white">Employees Can Edit Submitted Timesheets</h4>
                      <p className="text-xs text-slate-500 mt-0.5">Allow edits before approval is granted</p>
                    </div>
                    <Switch
                      checked={settings.employees_can_edit_submitted}
                      onCheckedChange={(checked) => setSettings(prev => ({ ...prev, employees_can_edit_submitted: checked }))}
                      data-testid="edit-submitted-toggle"
                    />
                  </div>
                </div>
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>
        )}

        {/* PRIORITY 1: Screen Capture Settings - Fix 50 */}
        <Collapsible defaultOpen={false}>
          <div className="premium-card" data-testid="screen-capture-section">
            <CollapsibleTrigger className="w-full p-6 flex items-center justify-between cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
              <div className="flex items-center gap-3">
                <Camera className="w-5 h-5 text-violet-600" />
                <div className="text-left">
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Screen Capture</h2>
                  <p className="text-sm text-slate-500">Configure employee screen monitoring</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="px-6 pb-6 space-y-6 border-t border-slate-100 dark:border-slate-800 pt-6">
                <div>
                  <Label className="text-sm font-medium">Screenshot Capture Frequency</Label>
                  <p className="text-xs text-slate-500 mb-3">How often to capture screenshots during active time tracking</p>
                  <Select
                    value={String(settings.screen_capture_frequency)}
                    onValueChange={(value) => setSettings(prev => ({ ...prev, screen_capture_frequency: parseInt(value) }))}
                  >
                    <SelectTrigger className="mt-2 h-11 w-full md:w-64" data-testid="capture-frequency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Every 1 minute</SelectItem>
                      <SelectItem value="5">Every 5 minutes</SelectItem>
                      <SelectItem value="10">Every 10 minutes (Recommended)</SelectItem>
                      <SelectItem value="30">Every 30 minutes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Screenshot Blur */}
                <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <Label className="text-sm font-medium">Screenshot Blur</Label>
                      <p className="text-xs text-slate-500">Apply blur to screenshots for privacy protection</p>
                    </div>
                    <Switch
                      checked={settings.screenshot_blur_enabled || false}
                      onCheckedChange={(checked) => setSettings(prev => ({ ...prev, screenshot_blur_enabled: checked }))}
                      data-testid="screenshot-blur-toggle"
                    />
                  </div>
                  {settings.screenshot_blur_enabled && (
                    <div className="mt-3">
                      <Label className="text-xs font-medium text-slate-500 mb-2 block">Blur Level</Label>
                      <div className="flex gap-3">
                        {[
                          { value: 'light', label: 'Light', desc: 'Slightly obscured', blur: '4px' },
                          { value: 'medium', label: 'Medium', desc: 'Moderately blurred', blur: '8px' },
                          { value: 'heavy', label: 'Heavy', desc: 'Fully obscured', blur: '16px' },
                        ].map(opt => (
                          <button
                            key={opt.value}
                            onClick={() => setSettings(prev => ({ ...prev, screenshot_blur_level: opt.value }))}
                            className={`flex-1 p-3 rounded-xl border-2 text-center transition-all ${
                              settings.screenshot_blur_level === opt.value
                                ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/10'
                                : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                            }`}
                            data-testid={`blur-${opt.value}`}
                          >
                            <div className="w-full h-8 rounded-md bg-gradient-to-r from-slate-200 to-slate-300 dark:from-slate-600 dark:to-slate-500 mb-2 overflow-hidden">
                              <div className="w-full h-full" style={{ backdropFilter: `blur(${opt.blur})`, WebkitBackdropFilter: `blur(${opt.blur})`, background: `rgba(255,255,255,0.1)` }}>
                                <div className="flex items-center gap-1 p-1">
                                  <div className="w-4 h-1 bg-slate-400 rounded" style={{ filter: `blur(${opt.blur})` }} />
                                  <div className="w-6 h-1 bg-slate-400 rounded" style={{ filter: `blur(${opt.blur})` }} />
                                </div>
                              </div>
                            </div>
                            <span className={`text-sm font-semibold ${
                              settings.screenshot_blur_level === opt.value ? 'text-violet-700 dark:text-violet-400' : 'text-slate-600 dark:text-slate-400'
                            }`}>{opt.label}</span>
                            <span className="block text-[10px] text-slate-400 mt-0.5">{opt.desc}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="p-4 bg-blue-50 dark:bg-blue-900/10 rounded-xl border border-blue-100 dark:border-blue-500/20">
                  <h4 className="text-sm font-medium text-blue-700 dark:text-blue-400 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Privacy Information
                  </h4>
                  <ul className="text-xs text-blue-600 dark:text-blue-300 mt-2 space-y-1 ml-6 list-disc">
                    <li>Employees must consent before screen capture is enabled</li>
                    <li>Captures are paused when the timer is paused</li>
                    <li>Only Managers and Admins can view screenshots</li>
                    <li>Screenshots are automatically deleted after 30 days</li>
                  </ul>
                </div>
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>


        {/* Tracking Policy */}
        <Collapsible defaultOpen={false}>
          <div className="premium-card" data-testid="tracking-policy-section">
            <CollapsibleTrigger className="w-full p-6 flex items-center justify-between cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-indigo-600" />
                <div className="text-left">
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Tracking Policy</h2>
                  <p className="text-xs text-slate-500">Configure how employees are tracked across your organization</p>
                </div>
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="px-6 pb-6 space-y-4">
                <div className="space-y-3">
                  {[
                    {
                      value: 'standard',
                      title: 'Standard',
                      desc: 'Admin picks per-employee tracking mode (normal or stealth). Full web access for all users.',
                      badge: 'Default'
                    },
                    {
                      value: 'full_stealth',
                      title: 'Full Stealth',
                      desc: 'All employees auto-tracked silently. No web access for employees. Admin enrolls devices with a one-time token.',
                      badge: 'Enterprise'
                    },
                    {
                      value: 'stealth_hr',
                      title: 'Stealth + HR',
                      desc: 'All employees auto-tracked silently. Employees can log into web for leave, timesheets, payslips. Admin enrolls devices with a token.',
                      badge: 'Recommended'
                    }
                  ].map(option => (
                    <label
                      key={option.value}
                      className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        settings.tracking_policy === option.value
                          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                      }`}
                      data-testid={`policy-${option.value}`}
                    >
                      <input
                        type="radio"
                        name="tracking_policy"
                        value={option.value}
                        checked={settings.tracking_policy === option.value}
                        onChange={() => setSettings(prev => ({ ...prev, tracking_policy: option.value }))}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm">{option.title}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                            option.value === 'standard' ? 'bg-slate-200 text-slate-600' :
                            option.value === 'full_stealth' ? 'bg-indigo-200 text-indigo-700' :
                            'bg-emerald-200 text-emerald-700'
                          }`}>{option.badge}</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">{option.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
                <Button
                  onClick={async () => {
                    try {
                      const { data } = await api.put('/workspace-settings/tracking-policy', { tracking_policy: settings.tracking_policy });
                      toast.success(data.message || 'Tracking policy updated');
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Failed to update tracking policy');
                    }
                  }}
                  className="w-full"
                  data-testid="save-tracking-policy"
                >
                  Save Tracking Policy
                </Button>
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>


        {/* PRIORITY 1: Idle Detection Settings - Fix 51 */}
        <Collapsible defaultOpen={false}>
          <div className="premium-card" data-testid="idle-detection-section">
            <CollapsibleTrigger className="w-full p-6 flex items-center justify-between cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-amber-600" />
                <div className="text-left">
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Idle Detection</h2>
                  <p className="text-sm text-slate-500">Configure inactivity alerts and auto-pause</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="px-6 pb-6 space-y-6 border-t border-slate-100 dark:border-slate-800 pt-6">
                
                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50">
                  <div>
                    <Label className="text-sm font-medium">Enable Idle Detection</Label>
                    <p className="text-xs text-slate-500">Alert employees when they've been inactive</p>
                  </div>
                  <Switch
                    checked={settings.idle_detection_enabled}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, idle_detection_enabled: checked }))}
                    data-testid="idle-detection-toggle"
                  />
                </div>

                {settings.idle_detection_enabled && (
                  <>
                    {/* Idle Threshold */}
                    <div>
                      <Label className="text-sm font-medium">Idle Threshold</Label>
                      <p className="text-xs text-slate-500 mb-3">How long before showing "Still working?" prompt</p>
                      <Select
                        value={String(settings.idle_threshold)}
                        onValueChange={(value) => setSettings(prev => ({ ...prev, idle_threshold: parseInt(value) }))}
                      >
                        <SelectTrigger className="h-11 w-full md:w-64" data-testid="idle-threshold-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="5">5 minutes</SelectItem>
                          <SelectItem value="10">10 minutes (Recommended)</SelectItem>
                          <SelectItem value="15">15 minutes</SelectItem>
                          <SelectItem value="20">20 minutes</SelectItem>
                          <SelectItem value="30">30 minutes</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Idle Action */}
                    <div>
                      <Label className="text-sm font-medium">Action After No Response</Label>
                      <p className="text-xs text-slate-500 mb-3">What happens if employee doesn't respond within 2 minutes</p>
                      <Select
                        value={settings.idle_action}
                        onValueChange={(value) => setSettings(prev => ({ ...prev, idle_action: value }))}
                      >
                        <SelectTrigger className="h-11 w-full md:w-64" data-testid="idle-action-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="warn">Warning only (no auto-action)</SelectItem>
                          <SelectItem value="auto_pause">Auto-pause timer (Recommended)</SelectItem>
                          <SelectItem value="auto_stop">Auto-stop timer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="p-4 bg-amber-50 dark:bg-amber-900/10 rounded-xl border border-amber-100 dark:border-amber-500/20">
                      <h4 className="text-sm font-medium text-amber-700 dark:text-amber-400 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        How Idle Detection Works
                      </h4>
                      <ul className="text-xs text-amber-600 dark:text-amber-300 mt-2 space-y-1 ml-6 list-disc">
                        <li>After {settings.idle_threshold} minutes of no activity, a "Still working?" prompt appears</li>
                        <li>Employee has 2 minutes to respond before the selected action is triggered</li>
                        <li>Employee can choose to: keep time, discard idle time, or mark as break</li>
                        <li>Screen capture is automatically paused when timer is paused</li>
                      </ul>
                    </div>
                  </>
                )}
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>

        {/* PRIORITY 1: Approval Workflow */}
        {!isFreePlan && (<>
        <Collapsible open={approvalOpen} onOpenChange={setApprovalOpen}>
          <div className="premium-card" data-testid="approval-workflow-section">
            <CollapsibleTrigger className="w-full p-6 flex items-center justify-between cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
              <div className="flex items-center gap-3">
                <Check className="w-5 h-5 text-amber-600" />
                <div className="text-left">
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Approval Workflow</h2>
                  <p className="text-sm text-slate-500">Configure who approves what</p>
                </div>
              </div>
              {approvalOpen ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="px-6 pb-6 space-y-6 border-t border-slate-100 dark:border-slate-800 pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-sm font-medium">Who Approves Timesheets</Label>
                    <Select
                      value={settings.timesheet_approver}
                      onValueChange={(value) => setSettings(prev => ({ ...prev, timesheet_approver: value }))}
                    >
                      <SelectTrigger className="mt-2 h-11" data-testid="timesheet-approver-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="direct_manager">Direct Manager</SelectItem>
                        <SelectItem value="any_admin">Any Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Who Approves Time Off</Label>
                    <Select
                      value={settings.time_off_approver}
                      onValueChange={(value) => setSettings(prev => ({ ...prev, time_off_approver: value }))}
                    >
                      <SelectTrigger className="mt-2 h-11" data-testid="timeoff-approver-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="direct_manager">Direct Manager</SelectItem>
                        <SelectItem value="any_admin">Any Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                    <div>
                      <h4 className="text-sm font-medium text-slate-900 dark:text-white">Time Off Requires Approval</h4>
                      <p className="text-xs text-slate-500 mt-0.5">Otherwise, time off is auto-approved</p>
                    </div>
                    <Switch
                      checked={settings.time_off_requires_approval}
                      onCheckedChange={(checked) => setSettings(prev => ({ ...prev, time_off_requires_approval: checked }))}
                      data-testid="timeoff-approval-toggle"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium">Maximum Time Off Days Per Request</Label>
                  <Input
                    type="number"
                    min="1"
                    max="30"
                    value={settings.max_time_off_days_per_request}
                    onChange={(e) => setSettings(prev => ({ ...prev, max_time_off_days_per_request: parseInt(e.target.value) || 14 }))}
                    className="mt-2 h-11 w-32"
                    data-testid="max-timeoff-days-input"
                  />
                </div>
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>

        {/* PRIORITY 2: Roles & Permissions */}
        <Collapsible open={rolesOpen} onOpenChange={setRolesOpen}>
          <div className="premium-card" data-testid="roles-permissions-section">
            <CollapsibleTrigger className="w-full p-6 flex items-center justify-between cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-indigo-600" />
                <div className="text-left">
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Roles & Permissions</h2>
                  <p className="text-sm text-slate-500">Configure role capabilities</p>
                </div>
              </div>
              {rolesOpen ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="px-6 pb-6 border-t border-slate-100 dark:border-slate-800 pt-6">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 dark:border-slate-700">
                        <th className="text-left py-3 px-4 font-medium text-slate-600 dark:text-slate-400">Permission</th>
                        <th className="text-center py-3 px-4 font-medium text-slate-600 dark:text-slate-400">Admin</th>
                        <th className="text-center py-3 px-4 font-medium text-slate-600 dark:text-slate-400">Manager</th>
                        <th className="text-center py-3 px-4 font-medium text-slate-600 dark:text-slate-400">Employee</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-slate-100 dark:border-slate-800">
                        <td className="py-3 px-4 text-slate-700 dark:text-slate-300">Create Projects</td>
                        <td className="py-3 px-4 text-center"><Check className="w-5 h-5 text-emerald-500 mx-auto" /></td>
                        <td className="py-3 px-4 text-center">
                          <Switch
                            checked={settings.manager_can_create_projects}
                            onCheckedChange={(checked) => setSettings(prev => ({ ...prev, manager_can_create_projects: checked }))}
                          />
                        </td>
                        <td className="py-3 px-4 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                      </tr>
                      <tr className="border-b border-slate-100 dark:border-slate-800">
                        <td className="py-3 px-4 text-slate-700 dark:text-slate-300">Approve Timesheets</td>
                        <td className="py-3 px-4 text-center"><Check className="w-5 h-5 text-emerald-500 mx-auto" /></td>
                        <td className="py-3 px-4 text-center">
                          <Switch
                            checked={settings.manager_can_approve_timesheets}
                            onCheckedChange={(checked) => setSettings(prev => ({ ...prev, manager_can_approve_timesheets: checked }))}
                          />
                        </td>
                        <td className="py-3 px-4 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                      </tr>
                      <tr className="border-b border-slate-100 dark:border-slate-800">
                        <td className="py-3 px-4 text-slate-700 dark:text-slate-300">Approve Time Off</td>
                        <td className="py-3 px-4 text-center"><Check className="w-5 h-5 text-emerald-500 mx-auto" /></td>
                        <td className="py-3 px-4 text-center">
                          <Switch
                            checked={settings.manager_can_approve_time_off}
                            onCheckedChange={(checked) => setSettings(prev => ({ ...prev, manager_can_approve_time_off: checked }))}
                          />
                        </td>
                        <td className="py-3 px-4 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                      </tr>
                      <tr className="border-b border-slate-100 dark:border-slate-800">
                        <td className="py-3 px-4 text-slate-700 dark:text-slate-300">View All Employee Reports</td>
                        <td className="py-3 px-4 text-center"><Check className="w-5 h-5 text-emerald-500 mx-auto" /></td>
                        <td className="py-3 px-4 text-center">
                          <Switch
                            checked={settings.manager_can_view_all_reports}
                            onCheckedChange={(checked) => setSettings(prev => ({ ...prev, manager_can_view_all_reports: checked }))}
                          />
                        </td>
                        <td className="py-3 px-4 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                      </tr>
                      <tr className="border-b border-slate-100 dark:border-slate-800">
                        <td className="py-3 px-4 text-slate-700 dark:text-slate-300">Manage People</td>
                        <td className="py-3 px-4 text-center"><Check className="w-5 h-5 text-emerald-500 mx-auto" /></td>
                        <td className="py-3 px-4 text-center">
                          <Switch
                            checked={settings.manager_can_manage_people}
                            onCheckedChange={(checked) => setSettings(prev => ({ ...prev, manager_can_manage_people: checked }))}
                          />
                        </td>
                        <td className="py-3 px-4 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-slate-700 dark:text-slate-300">Access Workspace Settings</td>
                        <td className="py-3 px-4 text-center"><Check className="w-5 h-5 text-emerald-500 mx-auto" /></td>
                        <td className="py-3 px-4 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                        <td className="py-3 px-4 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>

        {/* PRIORITY 3: Subscription & Billing */}
        <Collapsible open={billingOpen} onOpenChange={setBillingOpen}>
          <div className="premium-card" data-testid="billing-section">
            <CollapsibleTrigger className="w-full p-6 flex items-center justify-between cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
              <div className="flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-pink-600" />
                <div className="text-left">
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Subscription & Billing</h2>
                  <p className="text-sm text-slate-500">Plan details and billing</p>
                </div>
              </div>
              {billingOpen ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="px-6 pb-6 border-t border-slate-100 dark:border-slate-800 pt-6">
                {subscriptionInfo ? (
                  <div className="space-y-4">
                    <div className={`flex items-center justify-between p-6 rounded-2xl ${
                      subscriptionInfo.plan === 'active' 
                        ? 'bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20'
                        : subscriptionInfo.plan === 'trial'
                        ? 'bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20'
                        : 'bg-gradient-to-br from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20'
                    }`}>
                      <div>
                        <span className={`text-xs font-semibold uppercase tracking-wider ${
                          subscriptionInfo.plan === 'active' ? 'text-emerald-600 dark:text-emerald-400' :
                          subscriptionInfo.plan === 'trial' ? 'text-amber-600 dark:text-amber-400' :
                          'text-red-600 dark:text-red-400'
                        }`}>
                          {subscriptionInfo.plan === 'active' ? 'Active Subscription' : 
                           subscriptionInfo.plan === 'trial' ? (subscriptionInfo.is_valid ? 'Free Trial' : 'Trial Expired') :
                           'Inactive'}
                        </span>
                        {subscriptionInfo.payment_provider && (
                          <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-white dark:bg-slate-800/60 dark:bg-slate-700/60 text-slate-600 dark:text-slate-300 capitalize">
                            via {subscriptionInfo.payment_provider}
                          </span>
                        )}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                          <div>
                            <p className="text-xs text-slate-500">Active Users</p>
                            <p className="text-lg font-bold text-slate-900 dark:text-white">{subscriptionInfo.user_count}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Seat Limit</p>
                            <p className="text-lg font-bold text-slate-900 dark:text-white">{subscriptionInfo.billable_users}</p>
                          </div>
                          {subscriptionInfo.plan === 'active' && (
                            <>
                              <div>
                                <p className="text-xs text-slate-500">Monthly Cost</p>
                                <p className="text-lg font-bold text-slate-900 dark:text-white">₹{subscriptionInfo.monthly_cost}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500">Renews In</p>
                                <p className="text-lg font-bold text-slate-900 dark:text-white">{subscriptionInfo.days_remaining} days</p>
                              </div>
                            </>
                          )}
                          {subscriptionInfo.plan === 'trial' && subscriptionInfo.is_valid && (
                            <div>
                              <p className="text-xs text-slate-500">Trial Ends In</p>
                              <p className="text-lg font-bold text-slate-900 dark:text-white">{subscriptionInfo.days_remaining} days</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button 
                      onClick={() => navigate('/billing')}
                      className="btn-premium h-11"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Manage Billing
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <p className="text-sm">Unable to load subscription information</p>
                  </div>
                )}
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>

        {/* PRIORITY 3: Audit Log */}
        <Collapsible open={auditOpen} onOpenChange={setAuditOpen}>
          <div className="premium-card" data-testid="audit-log-section">
            <CollapsibleTrigger className="w-full p-6 flex items-center justify-between cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
              <div className="flex items-center gap-3">
                <History className="w-5 h-5 text-slate-600" />
                <div className="text-left">
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Audit Log</h2>
                  <p className="text-sm text-slate-500">Recent admin actions</p>
                </div>
              </div>
              {auditOpen ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="px-6 pb-6 border-t border-slate-100 dark:border-slate-800 pt-6">
                {auditLog.length > 0 ? (
                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    {auditLog.map((log, idx) => (
                      <div key={log.id || idx} className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                        <div 
                          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white font-medium text-xs"
                          style={{ backgroundColor: getAvatarColor(log.admin_name) }}
                        >
                          {getInitials(log.admin_name)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-900 dark:text-white">
                            <span className="font-medium">{log.admin_name}</span> {log.action}
                            {log.details && <span className="text-slate-500"> ({log.details})</span>}
                          </p>
                          <p className="text-xs text-slate-400 mt-0.5">{formatDate(log.created_at)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-8">No admin actions recorded yet</p>
                )}
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>
        </>)}

        {/* PRIORITY 2: Danger Zone */}
        <Collapsible open={dangerOpen} onOpenChange={setDangerOpen}>
          <div className="premium-card border-red-200 dark:border-red-900/50" data-testid="danger-zone-section">
            <CollapsibleTrigger className="w-full p-6 flex items-center justify-between cursor-pointer hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <div className="text-left">
                  <h2 className="text-lg font-semibold text-red-600">{t('workspace.dangerZone')}</h2>
                  <p className="text-sm text-red-500/70">Irreversible actions</p>
                </div>
              </div>
              {dangerOpen ? <ChevronDown className="w-5 h-5 text-red-400" /> : <ChevronRight className="w-5 h-5 text-red-400" />}
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="px-6 pb-6 space-y-4 border-t border-red-100 dark:border-red-900/30 pt-6">
                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                  <div>
                    <h4 className="text-sm font-medium text-slate-900 dark:text-white">Export All Data</h4>
                    <p className="text-xs text-slate-500 mt-0.5">Download all workspace data as CSV</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={handleExportData} data-testid="export-data-btn">
                    <Download className="w-4 h-4 mr-2" />
                    Export
                  </Button>
                </div>
                <div className="flex items-center justify-between p-4 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-900/50">
                  <div>
                    <h4 className="text-sm font-medium text-red-700 dark:text-red-400">Delete Workspace</h4>
                    <p className="text-xs text-red-500 mt-0.5">Permanently delete this workspace and all data</p>
                  </div>
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    onClick={() => setShowDeleteDialog(true)}
                    data-testid="delete-workspace-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>

        {/* Delete Workspace Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="sm:max-w-md rounded-2xl" data-testid="delete-workspace-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl text-red-600 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Delete Workspace
              </DialogTitle>
              <DialogDescription>
                This action cannot be undone. This will permanently delete your workspace and remove all data.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-xl">
                <p className="text-sm text-red-700 dark:text-red-400">
                  Please type <span className="font-bold">{settings.company_name || 'your workspace name'}</span> to confirm.
                </p>
              </div>
              <Input
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder="Type workspace name..."
                className="h-11"
                data-testid="delete-confirmation-input"
              />
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDeleteDialog(false);
                    setDeleteConfirmation('');
                  }}
                  className="flex-1 h-11"
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteWorkspace}
                  disabled={deleteConfirmation !== settings.company_name}
                  className="flex-1 h-11"
                  data-testid="confirm-delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Workspace
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default WorkspaceSettings;
